import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { findIntersections } from '../../../analytics/numerical-methods';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import { createEnumeratedCollection, memoizeByKey } from '../../../context/computed-collection';
import type { IntersectionParams } from './intersection.model';

/**
 * Creates intersection points between two functions.
 *
 * Uses numerical methods (bisection) to find where f(x) - g(x) = 0.
 * Points are reactive and update when either parent function changes.
 */
export class IntersectionCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'intersection';
  override readonly computedCollectionKeys = [{ name: '', properties: ['x', 'y'] }] as const;

  /** Maximum number of intersection points to find */
  private readonly maxIntersections = 10;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;

    // Get both parent elements from params (can be functions or derivatives)
    const params = def.params as IntersectionParams | undefined;
    if (!params?.of || !Array.isArray(params.of) || params.of.length !== 2) {
      throw new Error('Intersection feature requires params.of to be a tuple of two element IDs');
    }
    // Use getEvaluableParents to support both functions and derivatives
    const [fn1, fn2] = this.getEvaluableParents(params.of, context);
    const objects: GeometryElement[] = [];

    // Resolve styles using the feature style cascade (label is now part of style)
    const style = resolveFeatureStyle('point', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    // Memoized intersection finding
    const getIntersectionPoints = memoizeByKey(
      () => {
        const bbox = board.getBoundingBox();
        const xValues = findIntersections(fn1.evaluate!, fn2.evaluate!, bbox[0], bbox[2], {
          maxResults: this.maxIntersections,
        });
        return xValues.map((x) => ({ x, y: fn1.evaluate!(x) }));
      },
      () => {
        const bbox = board.getBoundingBox();
        const fn1Params = 'getParams' in fn1 ? (fn1 as { getParams: () => Record<string, number> }).getParams() : {};
        const fn2Params = 'getParams' in fn2 ? (fn2 as { getParams: () => Record<string, number> }).getParams() : {};
        return `${bbox[0].toFixed(2)},${bbox[2].toFixed(2)}|${Object.values(fn1Params)
          .map((v) => v.toFixed(4))
          .join(',')}|${Object.values(fn2Params)
          .map((v) => v.toFixed(4))
          .join(',')}`;
      },
    );

    // Create intersection points
    for (let i = 0; i < this.maxIntersections; i++) {
      const idx = i;
      const result = elementFactory.point({
        coords: [
          () => {
            const points = getIntersectionPoints();
            return idx < points.length ? points[idx].x : NaN;
          },
          () => {
            const points = getIntersectionPoints();
            return idx < points.length ? points[idx].y : NaN;
          },
        ],
        name: 'intersection',
        style: { ...style, visible },
      });
      objects.push(result.point);
      if (result.label) {
        objects.push(result.label);
      }
    }

    const collection = createEnumeratedCollection(getIntersectionPoints, this.maxIntersections);

    return {
      id,
      type: 'intersection',
      objects,
      getComputedCollections: () => ({ '': collection }),
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
